import { useState, useRef, useEffect, useCallback } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { sendMessage } from '../../services/api';
import MessagePending from './MessagePending';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faMessage, faX } from '@fortawesome/free-solid-svg-icons';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
}

const Chatbot = () => {
  const [isChatboxOpen, setIsChatboxOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { 
      id: '2', 
      text: "¡Bienvenido! 👋\n\nSoy el asistente virtual de Chambaticon, tu compañero inteligente para el crecimiento profesional. 🚀\n\nEstoy aquí para:\n• Facilitar tu búsqueda de empleo 💼\n• Proporcionarte información sobre vacantes disponibles 📋\n• Guiarte en cada paso del proceso ✨\n\n¿Qué te gustaría explorar primero?", 
      sender: 'bot' 
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const chatboxRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const [loadMessage, setLoadMessage] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isChatboxOpen &&
        chatContainerRef.current && 
        !chatContainerRef.current.contains(event.target as Node) &&
        toggleButtonRef.current &&
        !toggleButtonRef.current.contains(event.target as Node)
      ) {
        setIsChatboxOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside as any);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside as any);
    };
  }, [isChatboxOpen]);

  const toggleChatbox = () => {
    setIsChatboxOpen(!isChatboxOpen);
  };

  const handleSendMessage = async () => {
    try {
      if (inputValue.trim() === '') return;

      let newUserMessage: Message = {
        id: Date.now().toString(),
        text: inputValue,
        sender: 'user',
      };
      setMessages([...messages, newUserMessage]);
      setInputValue('');
      setLoadMessage(true);

      const response = await sendMessage(inputValue);
      if (!response.success) throw new Error(response.details || 'Error al enviar el mensaje');

      newUserMessage = {
        id: Date.now().toString(),
        text: response.data,
        sender: 'bot',
      };
      setMessages(prev => [...prev, newUserMessage]);
      setLoadMessage(false);
    } catch (error) {
      let newUserMessage: Message = {
        id: Date.now().toString(),
        text: 'En estos momentos no se ha podido procesar tu mensaje. Por favor, inténtalo más tarde.',
        sender: 'bot',
      };
      setMessages(prev => [...prev, newUserMessage]);
      setLoadMessage(false);
      console.error('Error al enviar el mensaje:', error);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  useEffect(() => {
    if (chatboxRef.current) {
      setTimeout(() => {
        chatboxRef.current!.scrollTop = chatboxRef.current!.scrollHeight;
      }, 50);
    }
  }, [messages, loadMessage, isChatboxOpen]);

  return (
    <>
      <div className="fixed bottom-0 right-0 mb-4 mr-4 z-[9999]">
        <button
          ref={toggleButtonRef}
          onClick={toggleChatbox}
          className="bg-Azul-Ticongle text-white p-4 rounded-full hover:bg-Azul-Ticongle/90 transition duration-300 flex items-center shadow-lg hover:shadow-xl"
          aria-label="Abrir chat"
        >
          <FontAwesomeIcon className="w-6 h-6" icon={isChatboxOpen ? faX : faMessage} />
        </button>
      </div>

      {isChatboxOpen && (
        <div className="fixed bottom-20 right-4 z-[9999] animate-fade-in" ref={chatContainerRef}>
          <div className="bg-white shadow-xl rounded-lg max-w-lg w-full border border-gray-200 overflow-hidden">
            <div className="p-4 border-b bg-Azul-Ticongle text-white flex justify-between items-center">
              <div>
                <p className="text-lg font-semibold">Asistente Virtual</p>
              </div>
              <button
                onClick={toggleChatbox}
                className="text-gray-300 hover:text-white focus:outline-none transition-colors duration-200"
                aria-label="Cerrar chat"
              >
                <FontAwesomeIcon icon={faX} className='w-5 h-5' />
              </button>
            </div>

            <div
              ref={chatboxRef}
              className="p-4 h-80 overflow-y-auto text-sm bg-gray-50"
            >
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`mb-4 ${message.sender === 'user' ? 'text-right' : ''}`}
                >
                  <div
                    className={`inline-block max-w-xs md:max-w-md rounded-2xl py-2 px-4 ${message.sender === 'user'
                      ? 'bg-Azul-Ticongle text-white rounded-br-none'
                      : 'bg-gray-200 text-gray-700 rounded-bl-none'
                      } whitespace-pre-wrap text-justify leading-6 shadow-sm`}
                  >
                    {message.text.split('\n').map((line, i) => {
                      const parts = line.split(/(Chambaticon)/gi);
                      return (
                        <span key={i}>
                          {parts.map((part, j) =>
                            part.toLowerCase() === 'chambaticon' ? 
                            <strong key={j} className="font-bold">{part}</strong> : part
                          )}
                          <br />
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
              {loadMessage && (
                <div className="flex justify-start items-center mt-4 text-gray-400">
                  <div className="bg-gray-200 text-gray-700 rounded-2xl rounded-bl-none py-2 px-4 shadow-sm flex">
                    <span className='pr-2 text-sm'>Esperando respuesta </span>
                    <MessagePending size="md" color="gray" />
                  </div>
                </div>
              )}
            </div>

            <div className="p-3 border-t flex bg-white">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Escribe tu pregunta..."
                className="w-full px-4 py-3 rounded-l-full focus:border-none focus:outline-none focus:ring-Azul-Ticongle/30 bg-gray-50 text-gray-700"
                autoFocus
              />
              <button
                onClick={handleSendMessage}
                disabled={inputValue.trim() === ''}
                className="bg-Azul-Ticongle text-white px-5 py-3 rounded-r-full hover:bg-Azul-Ticongle/90 disabled:opacity-50 disabled:cursor-not-allowed transition duration-300 flex items-center"
                aria-label="Enviar mensaje"
              >
                <FontAwesomeIcon icon={faPaperPlane} className='w-4 h-4' />
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default Chatbot;